import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from './useAuthStore';
import { config } from '../config';

// ---------------------------------------------------------------------------
// Generic raw WebSocket hook (kept for non-notification use cases)
// ---------------------------------------------------------------------------

export interface WebSocketMessage {
  type: string;
  data: unknown;
  timestamp: string;
}

export interface WebSocketOptions {
  url: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onMessage?: (message: WebSocketMessage) => void;
  onError?: (error: Event) => void;
  protocols?: string | string[];
}

export interface WebSocketState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  lastMessage: WebSocketMessage | null;
  reconnectCount: number;
}

export const useWebSocket = (options: WebSocketOptions) => {
  const {
    url,
    reconnectInterval = 3000,
    maxReconnectAttempts = 5,
    onConnect,
    onDisconnect,
    onMessage,
    onError,
    protocols,
  } = options;

  const [state, setState] = useState<WebSocketState>({
    isConnected: false,
    isConnecting: false,
    error: null,
    lastMessage: null,
    reconnectCount: 0,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const reconnectCountRef = useRef(0);
  const shouldReconnectRef = useRef(true);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setState((prev) => ({ ...prev, isConnecting: true, error: null }));

    try {
      const ws = new WebSocket(url, protocols);
      wsRef.current = ws;

      ws.onopen = () => {
        setState((prev) => ({
          ...prev,
          isConnected: true,
          isConnecting: false,
          error: null,
          reconnectCount: 0,
        }));
        reconnectCountRef.current = 0;
        onConnect?.();
      };

      ws.onclose = (event) => {
        setState((prev) => ({ ...prev, isConnected: false, isConnecting: false }));
        wsRef.current = null;
        onDisconnect?.();

        if (
          shouldReconnectRef.current &&
          event.code !== 1000 &&
          reconnectCountRef.current < maxReconnectAttempts
        ) {
          reconnectCountRef.current++;
          setState((prev) => ({ ...prev, reconnectCount: reconnectCountRef.current }));
          reconnectTimeoutRef.current = setTimeout(() => {
            if (shouldReconnectRef.current) connect();
          }, reconnectInterval);
        } else if (reconnectCountRef.current >= maxReconnectAttempts) {
          setState((prev) => ({ ...prev, error: 'Maximum reconnection attempts reached' }));
        }
      };

      ws.onerror = (error) => {
        setState((prev) => ({ ...prev, error: 'WebSocket connection error', isConnecting: false }));
        onError?.(error);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data as string);
          setState((prev) => ({ ...prev, lastMessage: message }));
          onMessage?.(message);
        } catch (_error) {
          console.error('Failed to parse WebSocket message:', _error);
        }
      };
    } catch {
      setState((prev) => ({
        ...prev,
        error: 'Failed to create WebSocket connection',
        isConnecting: false,
      }));
    }
  }, [
    url,
    protocols,
    onConnect,
    onDisconnect,
    onMessage,
    onError,
    reconnectInterval,
    maxReconnectAttempts,
  ]);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    setState((prev) => ({ ...prev, isConnected: false, isConnecting: false }));
  }, []);

  const sendMessage = useCallback((message: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(typeof message === 'string' ? message : JSON.stringify(message));
        return true;
      } catch (_error) {
        console.error('Failed to send WebSocket message:', _error);
        return false;
      }
    }
    return false;
  }, []);

  const reconnect = useCallback(() => {
    if (reconnectCountRef.current < maxReconnectAttempts) {
      shouldReconnectRef.current = true;
      disconnect();
      setTimeout(connect, 100);
    }
  }, [connect, disconnect, maxReconnectAttempts]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) wsRef.current.close(1000, 'Component unmounting');
    };
  }, [connect]);

  return { ...state, connect, disconnect, reconnect, sendMessage };
};

// ---------------------------------------------------------------------------
// Socket.io-based notification hook (M3)
// Connects to the NestJS Socket.io gateway with per-user room isolation.
// ---------------------------------------------------------------------------

export interface IncomingNotification {
  id: string;
  userId: string;
  subject: string;
  message: string;
  type: string;
  propertyId?: string;
  isRead: boolean;
  createdAt: string;
  readAt?: string;
}

export interface IncomingCrmMessage {
  leadId: number;
  messageId: number;
  body: string | null;
  from: string;
}

export interface NotificationSocketOptions {
  onNotification?: (notification: IncomingNotification) => void;
  onCrmMessage?: (message: IncomingCrmMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

export const useNotificationSocket = (options: NotificationSocketOptions = {}) => {
  const { onNotification, onCrmMessage, onConnect, onDisconnect } = options;

  const token = useAuthStore((state) => state.token);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token) return;

    const wsUrl = config.wsUrl || undefined;

    const socket = io(wsUrl, {
      transports: ['websocket'],
      auth: { token },
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      onConnect?.();
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
      onDisconnect?.();
    });

    socket.on('new-notification', (data: IncomingNotification) => {
      onNotification?.(data);
    });

    socket.on('crm:new-message', (data: IncomingCrmMessage) => {
      onCrmMessage?.(data);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // Re-connect if token changes (e.g. after token refresh)
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isConnected, socket: socketRef.current };
};

// ---------------------------------------------------------------------------
// Legacy alias, kept so any existing consumers don't break immediately
// ---------------------------------------------------------------------------

/** @deprecated Use useNotificationSocket instead */
export const useNotificationWebSocket = (
  onNotificationReceived: (notification: unknown) => void,
  onNotificationUpdate: (update: unknown) => void
) => {
  return useNotificationSocket({
    onNotification: (notification) => {
      if (!notification.isRead) {
        onNotificationReceived(notification);
      } else {
        onNotificationUpdate(notification);
      }
    },
  });
};
